import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useWorkspace } from '../app/WorkspaceProvider';
import { AppLink } from '../components/Nav';
import { VideoGrid, VideoGridSkeleton } from '../components/VideoCard';
import { LinkIcon, RecordIcon } from '../components/icons';
import { Banner, Button, EmptyState, ErrorState, PageHeader, cx } from '../components/ui';
import { errorMessage } from '../lib/api';
import { subscriptionNotice } from '../queries/orgs';
import { fetchLibrary, libraryScopeKey, type LibraryQuery } from '../queries/videos';
import { qk } from '../queries/keys';

/**
 * The library. Personal by default; the workspace switcher in the header
 * decides whether an org's team shelf is available alongside it.
 */

type Shelf = 'personal' | 'team';

export function DashboardPage() {
  const { activeOrg, loading: orgsLoading } = useWorkspace();
  const [shelf, setShelf] = useState<Shelf>('personal');

  const showTeam = activeOrg !== null;
  const active: Shelf = showTeam ? shelf : 'personal';

  const libraryQuery: LibraryQuery =
    active === 'team' && activeOrg
      ? { workspace: 'team', orgId: activeOrg.id, limit: 24 }
      : { limit: 24 };

  const query = useInfiniteQuery({
    queryKey: qk.videos(libraryScopeKey(libraryQuery)),
    queryFn: ({ pageParam }) => fetchLibrary({ ...libraryQuery, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 20_000,
  });

  const videos = query.data?.pages.flatMap((page) => page.videos) ?? [];
  const notice = activeOrg
    ? subscriptionNotice(activeOrg.subscriptionStatus, activeOrg.trialEndsAt)
    : null;

  return (
    <>
      <PageHeader
        title={active === 'team' && activeOrg ? activeOrg.name : 'Your library'}
        subtitle={
          active === 'team'
            ? 'Everything the team has shared to this workspace.'
            : 'Everything you have recorded. Free forever, yours alone.'
        }
        actions={
          <AppLink to="/new">
            <Button variant="primary" className="gap-2">
              <RecordIcon size={11} className="text-record" />
              New recording
            </Button>
          </AppLink>
        }
      />

      {notice && active === 'team' ? (
        <div className="mb-5">
          <Banner tone={notice.tone} title={notice.title}>
            {notice.body}
          </Banner>
        </div>
      ) : null}

      {showTeam && activeOrg ? (
        <div className="mb-5 inline-flex rounded border border-hairline bg-slate-850 p-0.5">
          {(
            [
              ['personal', 'Personal'],
              ['team', activeOrg.name],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setShelf(value)}
              className={cx(
                'rounded-xs px-3 py-1.5 text-body transition-colors duration-120',
                active === value ? 'bg-slate-700 text-chalk' : 'text-chalk-dim hover:text-chalk',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {query.isLoading || orgsLoading ? <VideoGridSkeleton /> : null}

      {query.isError ? (
        <ErrorState
          title="Your library did not load"
          message={errorMessage(query.error)}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {!query.isLoading && !query.isError && videos.length === 0 ? (
        active === 'team' ? (
          <EmptyState title="Nothing in the team workspace yet">
            Recordings land here when someone sets "Save to" to this team before hitting record.
            Personal recordings stay personal unless they are moved.
          </EmptyState>
        ) : (
          <EmptyState
            title="No recordings yet"
            icon={<RecordIcon size={22} />}
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <AppLink to="/new">
                  <Button variant="primary">Record something</Button>
                </AppLink>
                <AppLink to="/links">
                  <Button variant="ghost" className="gap-2">
                    <LinkIcon size={14} />
                    Ask someone else for one
                  </Button>
                </AppLink>
              </div>
            }
          >
            Pick a screen, talk over it, send the link. It takes about ninety seconds to replace
            the meeting you were going to schedule.
          </EmptyState>
        )
      ) : null}

      {videos.length > 0 ? <VideoGrid videos={videos} /> : null}

      {query.hasNextPage ? (
        <div className="mt-6 flex justify-center">
          <Button onClick={() => void query.fetchNextPage()} busy={query.isFetchingNextPage}>
            Load more
          </Button>
        </div>
      ) : null}
    </>
  );
}
